<script setup lang="ts">
/**
 * Carte d'une cagnotte — rayon /recherche/cagnottes, accueil, suggestions.
 * Présentation « vitrine » commune (VitrineCarte, Karim 2026-10-12) : montant
 * collecté, donateurs, objectif et barre de progression posés sur l'image.
 */
import { themeAffiche } from "~/utils/themesAffiches";

/** Seuls les champs affichés : l'accueil passe une version allégée. */
interface CagnotteCarte {
  slug: string;
  title: string;
  image: string | null;
  /** Colonne NUMERIC : l'API la renvoie parfois en texte (« 30000.00 »). */
  goal_amount: number | string | null;
  raised: number | string | null;
  donors_count: number | string | null;
  currency?: string | null;
  category?: string | null;
  organization_name: string;
  organization_logo: string | null;
}

const props = defineProps<{ campaign: CagnotteCarte; priority?: boolean }>();

const { formatCurrency } = useFormatLangue();

const devise = computed(() => props.campaign.currency || "EUR");
const objectif = computed(() => Number(props.campaign.goal_amount) || 0);
const collecte = computed(() => Number(props.campaign.raised) || 0);
const donateurs = computed(() => Number(props.campaign.donors_count) || 0);

const formatMontant = (montant: number) =>
  formatCurrency(montant, { currency: devise.value, decimals: 0 });

/**
 * Remplissage de la barre, borné à 100 % (une cagnotte peut dépasser). Un
 * premier don reste visible (2 % minimum) au lieu d'une barre vide.
 */
const progression = computed(() => {
  if (objectif.value <= 0 || collecte.value <= 0) return 0;
  return Math.min(100, Math.max(2, (collecte.value / objectif.value) * 100));
});

/** Thème ; rien pour « Autre » (n'apprend rien). */
const pastille = computed(() =>
  props.campaign.category && props.campaign.category !== "autre"
    ? themeAffiche(props.campaign.category)
    : "",
);
</script>

<template>
  <VitrineCarte
    :href="`/c/${campaign.slug}`"
    :titre="campaign.title"
    :image="campaign.image"
    :pastille="pastille"
    :association-nom="campaign.organization_name"
    :association-logo="campaign.organization_logo"
    :action="$t('commun.vitrine.actionCollecte')"
    :priority="priority"
  >
    <div class="flex items-baseline justify-between gap-3">
      <p class="min-w-0 truncate text-sm text-white/80">
        <span class="text-lg sm:text-xl text-white" style="font-weight: 700 !important">{{
          formatMontant(collecte)
        }}</span>
        <span aria-hidden="true"> · </span>
        {{ $t("commun.vitrine.donateurs", { n: donateurs }, donateurs) }}
      </p>
      <p v-if="objectif > 0" class="shrink-0 text-sm text-white/70">
        {{ $t("commun.vitrine.surObjectif", { objectif: formatMontant(objectif) }) }}
      </p>
    </div>
    <div
      v-if="objectif > 0"
      class="mt-3 h-2 rounded-full bg-white/25 overflow-hidden"
      role="progressbar"
      :aria-valuenow="Math.round(progression)"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="$t('commun.vitrine.surObjectif', { objectif: formatMontant(objectif) })"
    >
      <div
        class="h-full rounded-full transition-[width] duration-700 ease-out"
        :style="{
          width: `${progression}%`,
          background: 'linear-gradient(to right, color-mix(in srgb, var(--color-main2) 70%, white), color-mix(in srgb, var(--color-main2) 35%, white))',
        }"
      />
    </div>
  </VitrineCarte>
</template>
